import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_WHOLESALE_VIEW, isWholesaleView, type WholesaleView } from "./presentation.js";

/**
 * The wholesale workspace keeps its visible state in the address, like the
 * service one (`useWorkspaceUrlState`):
 *
 * - `view`     — which list is shown (`open` when absent or unknown);
 * - `order`    — the selected order, open in the inspector;
 * - `q`        — the search (the Search list's query);
 * - `customer` — the list narrowed to one customer (their order history).
 *
 * So a reload, a shared link or the Back button restores exactly what was
 * on screen, and each side keeps its own place (spec §4.1). Off means
 * absent: a blank search, no customer and no selection never appear.
 *
 * The Search list never carries a customer: its rows come from
 * `GET /api/wholesale/orders/search`, which knows no customer, so an
 * address that claims one would show a narrowing the rows do not have.
 * Opening Search drops the customer, and `view=search&customer=…` reads
 * as no customer.
 */

export type WholesaleUrlState = Readonly<{
  view: WholesaleView;
  orderId: string | null;
  query: string;
  customerId: string | null;
}>;

export const WHOLESALE_URL_PARAM = {
  view: "view",
  order: "order",
  query: "q",
  customer: "customer",
} as const;

export function parseWholesaleUrlState(params: URLSearchParams): WholesaleUrlState {
  const rawView = params.get(WHOLESALE_URL_PARAM.view);
  const view = isWholesaleView(rawView) ? rawView : DEFAULT_WHOLESALE_VIEW;
  const orderId = params.get(WHOLESALE_URL_PARAM.order);
  const customerId = params.get(WHOLESALE_URL_PARAM.customer);
  return {
    view,
    orderId: orderId ? orderId : null,
    query: (params.get(WHOLESALE_URL_PARAM.query) ?? "").trim(),
    customerId: customerId && carriesCustomer(view) ? customerId : null,
  };
}

/** Every list but Search can be narrowed to one customer's orders. */
export function carriesCustomer(view: WholesaleView): boolean {
  return view !== "search";
}

/** `view` always travels (it is the address of a list); everything else only when set. */
export function serializeWholesaleUrlState(state: WholesaleUrlState): URLSearchParams {
  const params = new URLSearchParams();
  params.set(WHOLESALE_URL_PARAM.view, state.view);
  const query = state.query.trim();
  if (query) params.set(WHOLESALE_URL_PARAM.query, query);
  if (state.customerId && carriesCustomer(state.view)) params.set(WHOLESALE_URL_PARAM.customer, state.customerId);
  if (state.orderId) params.set(WHOLESALE_URL_PARAM.order, state.orderId);
  return params;
}

export interface WholesaleUrlStateApi {
  state: WholesaleUrlState;
  /** Shows another list. The search and the customer travel along (Search takes no customer); the selection is dropped. */
  setView(view: WholesaleView): void;
  /** Opens an order in the inspector (`null` closes it). A history entry, so Back returns to the previous selection. */
  selectOrder(orderId: string | null): void;
  /** Replaces the search; typing never litters the history. */
  setQuery(query: string): void;
  setCustomer(customerId: string | null): void;
}

export function useWholesaleUrlState(): WholesaleUrlStateApi {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo(() => parseWholesaleUrlState(searchParams), [searchParams]);

  // Writes build on the last state *written* until the address has caught
  // up with it — the same guard as the service workspace, for the same
  // reason (a search committing on blur and the click that caused it).
  const pending = useRef<WholesaleUrlState | null>(null);
  const inFlight = useRef<Set<string>>(new Set());

  useEffect(() => {
    const landed = searchParams.toString();
    if (inFlight.current.delete(landed)) {
      if (inFlight.current.size === 0) pending.current = null;
      return;
    }
    inFlight.current.clear();
    pending.current = null;
  }, [searchParams]);

  const update = useCallback(
    (patch: (current: WholesaleUrlState) => WholesaleUrlState, options: { replace: boolean }) => {
      const base = pending.current ?? parseWholesaleUrlState(searchParams);
      const next = patch(base);
      const serialized = serializeWholesaleUrlState(next);
      pending.current = next;
      inFlight.current.add(serialized.toString());
      setSearchParams(serialized, { replace: options.replace });
    },
    [searchParams, setSearchParams],
  );

  const setView = useCallback(
    (view: WholesaleView) =>
      update(
        (current) => ({ ...current, view, orderId: null, customerId: carriesCustomer(view) ? current.customerId : null }),
        { replace: false },
      ),
    [update],
  );
  const selectOrder = useCallback(
    (orderId: string | null) => update((current) => ({ ...current, orderId }), { replace: false }),
    [update],
  );
  const setQuery = useCallback(
    (query: string) => update((current) => ({ ...current, query }), { replace: true }),
    [update],
  );
  const setCustomer = useCallback(
    (customerId: string | null) => update((current) => ({ ...current, customerId }), { replace: true }),
    [update],
  );

  return useMemo(
    () => ({ state, setView, selectOrder, setQuery, setCustomer }),
    [state, setView, selectOrder, setQuery, setCustomer],
  );
}
